import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBolt,
  FaStar,
  FaTrash,
  FaArrowLeft,
  FaHeart,
  FaCheck,
} from "react-icons/fa";
// --- IMPORTS ---
import Header from "./header";
import Footer from "./Footer";
import ProductDetailsView from "./ProductDetailsView";
import { fetchCart } from "../services/UserService";
import { Helmet } from "react-helmet-async";

const API_BASE = process.env.REACT_APP_API_URL;
const usingMock = false;

const WishlistPage = ({
  products = [],
  wishlistIds: initialWishlistIds,
  onProductClick,
}) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentView, setCurrentView] = useState("home");
  const [loading, setLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(() => {
    const stored = JSON.parse(localStorage.getItem("wishlist") || "[]");
    return new Set(stored);
  });

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // ... (keep all your existing logic - handleProductClick, toggleWishlist, fetchCartCount, etc.)
  const handleProductClick = (product) => {
    navigate(`/product-details/${slugify(product.name)}/${product.id}`);
  };
  const controller = new AbortController();

  // Load user data
  useEffect(() => {
    const storedData = localStorage.getItem("userData");
    if (storedData) setUser(JSON.parse(storedData));
  }, []);

  const USER_ID = user?.email;

  useEffect(() => {
    if (USER_ID === null) return;

    fetchInitialData();
  }, [USER_ID]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Load wishlist from localStorage
      const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      setWishlistIds(new Set(localWishlist));

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      try {
        // Always fetch banners and products
        const productPromise = fetch(`${API_BASE}/get-productList`, {
          signal: controller.signal,
        });

        // Only fetch wishlist if USER_ID exists
        const wishlistPromise = USER_ID
          ? fetch(`${API_BASE}/get-wishlist`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ user_identifier: USER_ID }),
              signal: controller.signal,
            })
          : Promise.resolve({
              json: async () => ({ success: false, data: [] }),
            }); // fallback empty response

        const [productRes, wishlistRes] = await Promise.all([
          productPromise,
          wishlistPromise,
        ]);

        clearTimeout(timeoutId);

        const productData = await productRes.json();
        const wishlistData = await wishlistRes.json();
        if (productData.success && Array.isArray(productData.data)) {
          const formatted = productData.data.map((p) => ({
            id: p.productid,
            name: p.productname,
            price: p.price,
            mrp_price: p.mrp_price,
            rating: p.rating,
            category: p.product_usage?.toLowerCase(),
            spec: `${p.power} ${p.Type}`,
            image: p.imageurl,
            description: p.description,
            applications: p.applications,
          }));
          setAllProducts(formatted);
        }

        if (wishlistData.success) {
          const apiWishlist = wishlistData.data.map(
            (i) => i.product_id || i.productid,
          );

          const localWishlist = JSON.parse(
            localStorage.getItem("wishlist") || "[]",
          );

          const mergedWishlist = new Set([...apiWishlist, ...localWishlist]);

          setWishlistIds(mergedWishlist);

          localStorage.setItem("wishlist", JSON.stringify([...mergedWishlist]));
        }
      } catch (err) {
        console.warn("API failed → using MOCK data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle wishlist
  const toggleWishlist = async (product) => {
    const isLiked = wishlistIds.has(product.id);

    setWishlistIds((prev) => {
      const newSet = new Set(prev);
      if (isLiked) newSet.delete(product.id);
      else newSet.add(product.id);
      localStorage.setItem("wishlist", JSON.stringify([...newSet]));
      return newSet;
    });

    if (!usingMock && USER_ID) {
      try {
        const url = isLiked
          ? `${API_BASE}/remove-from-wishlist`
          : `${API_BASE}/add-to-wishlist`;
        const body = {
          product_id: product.id,
          product_name: product.name,
          user_identifier: USER_ID,
        };
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch((e) => console.error("Wishlist sync failed", e));
      } catch (error) {
        console.error("API error:", error);
      }
    }
  };
  // Initial cart load
  const [cartCount, setCartCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const previousCartCountRef = useRef(0);

  const wishlistProducts = allProducts.filter((product) =>
    wishlistIds.has(product.id),
  );

  // Fetch cart count from API
  const fetchCartCount = async (showNotif = false) => {
    let newCount = 0;

    if (!USER_ID) {
      // Fallback to localStorage if no user
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      newCount = savedCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    } else {
      try {
        const response = await fetchCart(USER_ID);
        if (response && Array.isArray(response.items)) {
          newCount = response.items.reduce(
            (sum, item) => sum + (item.quantity || 1),
            0,
          );
        }
      } catch (error) {
        console.error("Error fetching cart count:", error);
        // Fallback to localStorage
        const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
        newCount = savedCart.reduce(
          (sum, item) => sum + (item.quantity || 1),
          0,
        );
      }
    }

    // Check if count increased and show notification
    if (
      showNotif &&
      newCount > previousCartCountRef.current &&
      previousCartCountRef.current >= 0
    ) {
      setShowNotification(true);
      setNotificationMessage("Item added to cart!");
      setTimeout(() => setShowNotification(false), 3000);
    }
    previousCartCountRef.current = newCount;
    setCartCount(newCount);
    return newCount;
  };

  // Load cart count on mount and when user changes
  useEffect(() => {
    // Initial fetch
    fetchCartCount();

    // Poll for cart updates every 3 seconds
    const interval = setInterval(() => {
      fetchCartCount(true); // Show notification if count increased
    }, 3000);

    // Listen for cart-change events
    const handleCartChange = () => {
      fetchCartCount(true);
    };

    window.addEventListener("cart-change", handleCartChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("cart-change", handleCartChange);
    };
  }, [USER_ID]);

  const handleAddToCart = () => {
    setCartCount((prev) => prev + 1);
    setShowNotification(true);
    setNotificationMessage("Item added to cart!");
    setTimeout(() => setShowNotification(false), 3000);
  };

  const refreshCartCount = () => {
    fetchCartCount();
  };

  // --- STYLES ---
  const styles = {
    loadingBox: {
      height: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#64748B",
    },

    // Footer Wrapper
    footerWrapper: { width: "100%", marginTop: "auto" },
    cardPriceBlock: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    cardPriceRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    cardPrice: {
      fontSize: "22px",
      fontWeight: "800",
      color: "#0F172A",
    },
    cardMrp: {
      fontSize: "14px",
      textDecoration: "line-through",
      color: "#94A3B8",
    },
    cardDiscount: {
      background: "#DCFCE7",
      color: "#166534",
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: "700",
    },
    cardVariants: {
      fontSize: "12px",
      color: "#d60909",
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
    },

    // States
    emptyState: {
      display: "flex",
      alignItems: "center",
      padding: "80px 0",
      marginLeft: "35%",
      textAlign: "center",
      height: "320px",
    },

    // 4x4 Grid Layout
    gridLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "25px",
      marginBottom: "50px",
    },
  };

  // ProductCard component with responsive improvements
  const ProductCard = ({
    item,
    onClick,
    addToCart,
    toggleWishlist,
    isWishlisted = true,
    isWishlistMode = true,
  }) => {
    const [hover, setHover] = useState(false);
    const imageSource = item.image || null;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
          position: "relative",
          transform: hover ? "translateY(-8px)" : "translateY(0)",
          boxShadow: hover
            ? "0 15px 30px rgba(0,0,0,0.1)"
            : "0 2px 10px rgba(0,0,0,0.04)",
          cursor: "pointer",
          border: "1px solid #F1F5F9",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onClick}
        className="fade-in"
      >
        {/* Image Area - Responsive */}
        <div
          style={{
            height: "200px",
            background: "#F8FAFC",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            overflow: "hidden",
          }}
        >
          {item.spec && !isWishlistMode && (
            <div
              style={{
                position: "absolute",
                top: "15px",
                left: "15px",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(4px)",
                padding: "4px 10px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: "700",
                color: "#1E293B",
                zIndex: 2,
              }}
            >
              {item.spec}
            </div>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation();
              toggleWishlist(item);
            }}
            style={{
              position: "absolute",
              top: "15px",
              right: "15px",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
              color: isWishlistMode
                ? "#EF4444"
                : isWishlisted
                  ? "#D60000"
                  : "#94A3B8",
              zIndex: 2,
              cursor: "pointer",
              transition: "0.2s",
            }}
          >
            {isWishlistMode ? (
              <FaTrash style={{ fontSize: "12px" }} />
            ) : (
              <FaHeart
                className={isWishlisted ? "heart-anim" : ""}
                style={{ fontSize: "14px" }}
              />
            )}
          </div>
          {imageSource ? (
            <img
              src={imageSource}
              alt={item.name}
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                transition: "transform 0.5s ease",
                transform: hover ? "scale(1.05)" : "scale(1)",
              }}
            />
          ) : (
            <FaBolt style={{ fontSize: "60px", color: "#CBD5E1" }} />
          )}
        </div>

        {/* Info Area - Responsive */}
        <div
          style={{
            padding: "18px",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#F59E0B",
                }}
              >
                <FaStar /> {item.rating || 4.5}
              </div>
            </div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#0F172A",
                marginBottom: "8px",
                lineHeight: "1.4",
                height: "42px",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item.name}
            </div>
            {item.description && (
              <div
                style={{
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: "1.5",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.description}
              </div>
            )}
          </div>

          {/* Bottom Price Section */}
          <div
            style={{
              marginTop: "12px",
              borderTop: "1px dashed #F1F5F9",
              paddingTop: "12px",
            }}
          >
            {/* Price info stays the same */}
            {/* ... your existing price section ... */}
            <div style={styles.cardPriceBlock}>
              {/* Price Row */}
              <div style={styles.cardPriceRow}>
                <div style={styles.cardPrice}>
                  ₹{Number(item?.price ?? 0).toLocaleString()}
                </div>

                {Number(item?.mrp_price ?? 0) > Number(item?.price ?? 0) && (
                  <>
                    <div style={styles.cardMrp}>
                      ₹{Number(item?.mrp_price ?? 0).toLocaleString()}
                    </div>
                    <div style={styles.cardDiscount}>
                      {Math.round(
                        ((item.mrp_price - item.price) / item.mrp_price) * 100,
                      )}
                      % OFF
                    </div>
                  </>
                )}
              </div>

              {/* Variants / Connectivity */}
              {(item.connectivity || item.total_variants) && (
                <div style={styles.cardVariants}>
                  {item.connectivity && <span>{item.connectivity}</span>}
                  {item.total_variants > 1 && (
                    <span>+ {item.total_variants - 1} more</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ marginTop: "10px" }}>
              <h5
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0F172A",
                  margin: 0,
                }}
              >
                Compatibility
              </h5>
            </div>

            <div
              style={{
                fontSize: "12px",
                marginTop: "4px",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
                color: "#475569",
              }}
            >
              {item.applications ? item.applications : "-"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* COMPREHENSIVE RESPONSIVE STYLES */}
      <style>{`
        /* BASE ANIMATIONS */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes heartBeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
        .heart-anim { animation: heartBeat 0.6s ease-in-out; }
        .fade-in { animation: fadeIn 0.6s ease-out; }

        /* RESPONSIVE GRID SYSTEM */
        .wishlist-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 25px;
          margin-bottom: 50px;
        }

        /* DESKTOP (1400px+) */
        @media (min-width: 1400px) {
          .wishlist-grid { 
            grid-template-columns: repeat(4, 1fr); 
            gap: 30px;
          }
        }

.wishlist-header {
  margin-left: 200px 
}


        /* LAPTOP (1201px - 1399px) */
        @media (max-width: 1399px) and (min-width: 1201px) {
          .wishlist-grid { 
            grid-template-columns: repeat(3, 1fr); 
            gap: 25px;
          }
        }

        /* TABLET LARGE (1025px - 1200px) */
        @media (max-width: 1200px) and (min-width: 1025px) {
          .wishlist-grid { 
            grid-template-columns: repeat(3, 1fr); 
            gap: 22px;
          }
          .wishlist-container { 
            padding-left: 250px !important; 
            padding-right: 25px !important;
          }
        }

        /* TABLET MEDIUM (901px - 1024px) */
        @media (max-width: 1024px) and (min-width: 901px) {
          .wishlist-grid { 
            grid-template-columns: repeat(2, 1fr); 
            gap: 20px;
          }
              .wishlist-empty { 
            margin-left: 0 !important; 
            width: 90% !important;
            left: 5% !important;
          }
          .wishlist-container { 
            padding-left: 200px !important; 
            padding-right: 20px !important;
          }
          .wishlist-header h1 { font-size: 28px !important; }
        }

        /* TABLET SMALL (769px - 900px) */
        @media (max-width: 900px) and (min-width: 769px) {
          .wishlist-grid { 
            grid-template-columns: repeat(2, 1fr); 
            gap: 18px;
          }
          .wishlist-container { 
            padding-left: 20px !important; 
            padding-right: 20px !important;
            max-width: 100% !important;
          }
          .wishlist-header { 
            margin-bottom: 30px !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .wishlist-header h1 { font-size: 26px !important; }
        }

        /* MOBILE LARGE (601px - 768px) */
        @media (max-width: 768px) and (min-width: 601px) {
          .wishlist-container { 
            padding-left: 20px !important; 
            padding-right: 20px !important;
          }
          .wishlist-grid { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 16px !important;
          }
          .wishlist-header h1 { font-size: 24px !important; }
          .wishlist-empty { 
            margin-left: 0 !important; 
            width: 90% !important;
            left: 5% !important;
          }
        }

        /* MOBILE MEDIUM (481px - 600px) */
        @media (max-width: 600px) and (min-width: 481px) {
          .wishlist-container { 
            padding-left: 15px !important; 
            padding-right: 15px !important;
          }
          .wishlist-grid { 
            grid-template-columns: 1fr !important; 
            gap: 15px !important;
          }
          .wishlist-header h1 { font-size: 22px !important; }
          .wishlist-empty { 
            margin-left: 0 !important; 
            width: 95% !important;
            left: 2.5% !important;
            height: 350px !important;
          }
        }

        /* MOBILE SMALL (0px - 480px) */
        @media (max-width: 480px) {
          .wishlist-container { 
            padding-left: 12px !important; 
            padding-right: 12px !important;
          }
          .wishlist-grid { 
            grid-template-columns: 1fr !important; 
            gap: 12px !important;
          }
          .wishlist-header { 
            margin-bottom: 25px !important;
            padding: 0 5px !important;
           margin-left: 10px !important;
          }
          .wishlist-header h1 { 
            font-size: 20px !important; 
            margin: 0 !important;
          }
          .wishlist-empty { 
            margin-left: 0 !important; 
            width: 80% !important;
            left: 0 !important;
            height: 320px !important;
            padding: 20px !important;
          }
          /* Product card adjustments */
          div[style*="height: 200px"] { height: 160px !important; }
          div[style*="fontSize: 15px"] { font-size: 14px !important; }
          div[style*="fontSize: 22px"] { font-size: 20px !important; }
        }

        /* CART NOTIFICATION RESPONSIVE */
        @media (max-width: 768px) {
          .floating-cart-icon {
            bottom: 20px !important;
            right: 20px !important;
          }
          .floating-cart-icon > div {
            width: 50px !important;
            height: 50px !important;
          }
          .cart-icon-svg { font-size: 20px !important; }
          .cart-notification {
            bottom: 80px !important;
            right: 20px !important;
            min-width: 160px !important;
            padding: 10px 14px !important;
            font-size: 13px !important;
          }
        }

        @media (max-width: 480px) {
          .cart-notification {
            bottom: 75px !important;
            right: 15px !important;
            min-width: 140px !important;
            padding: 8px 12px !important;
            font-size: 12px !important;
          }
        }

        /* EMPTY STATE RESPONSIVE */
        @media (max-width: 900px) {
          .wishlist-empty {
            margin-left: 0 !important;
            margin-top: 50 !important;
            left: 5vw !important;
            height: 350px !important;
          }
          .wishlist-empty h2 { font-size: 28px !important; }
          .wishlist-empty p { font-size: 16px !important; }
        }

        @media (max-width: 600px) {
          .wishlist-empty {
          margin-top: 50px !important;
            height: 450px !important;
            padding: 30px 20px !important;
          }
          .wishlist-empty h2 { font-size: 24px !important; }
          .wishlist-empty p { font-size: 15px !important; }
          .wishlist-empty button {
            padding: 14px 24px !important;
            font-size: 15px !important;
          }
        }

      `}</style>

      {/* {currentView === "product-details" && selectedProduct ? ( */}
      {loading ? (
        <div style={styles.loadingBox}>
          <div style={{ fontSize: "20px", fontWeight: "600" }}>
            Loading wishlist...
          </div>
        </div>
      ) : currentView === "product-details" && selectedProduct ? (
        <ProductDetailsView
          product={selectedProduct}
          onBack={() => setCurrentView("home")}
          addToCart={() => {
            handleAddToCart();
            refreshCartCount();
          }}
          toggleWishlist={toggleWishlist}
          isWishlisted={wishlistIds.has(selectedProduct.id)}
        />
      ) : (
        <>
          <Helmet>
            <title>My Wishlist | CGRMART</title>

            <meta
              name="description"
              content="View your saved EV chargers and products in your wishlist. Easily manage and purchase your favorite items."
            />

            <meta
              name="keywords"
              content="wishlist, EV chargers, saved products, Tucker EV, electric vehicle charging"
            />

            <meta name="robots" content="index, follow" />

            {/* Open Graph */}
            <meta property="og:title" content="My Wishlist | CGRMART" />
            <meta
              property="og:description"
              content="Your saved EV charging products and wishlist items."
            />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://cgrmart.com/wishlist" />

            {/* Twitter */}
            <meta name="twitter:title" content="My Wishlist | CGRMART" />
            <meta
              name="twitter:description"
              content="Check your saved EV charging products."
            />

            {/* Canonical */}
            <link rel="canonical" href="https://cgrmart.com/wishlist" />
          </Helmet>

          <Header />

          <div
            className="wishlist-container"
            style={{
              // paddingTop: "30px",
              paddingRight: "30px",
              margin: "0 auto",
              maxWidth: "1600px",
              marginTop: "100px",
            }}
          >
            <div className="fade-in">
              <div
                className="wishlist-header"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "40px",
                  gap: "15px",
                }}
              >
                <button
                  onClick={() => {
                    navigate("/");
                  }}
                  style={{
                    background: "white",
                    border: "1px solid #E2E8F0",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaArrowLeft />
                </button>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "32px",
                    fontWeight: "800",
                  }}
                >
                  My Wishlist
                </h1>
              </div>

              {!loading &&
              allProducts.length > 0 &&
              wishlistProducts.length === 0 ? (
                <div
                  className="wishlist-empty fade-in"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "80px 0",
                    marginLeft: "35%",
                    textAlign: "center",
                    height: "400px",
                    width: "600px",
                    background:
                      "linear-gradient(135deg, #e23552 0%, #0e0f0f 100%)",
                    borderRadius: "24px",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  {/* Animated Background Elements */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-50%",
                      right: "-20%",
                      width: "300px",
                      height: "300px",
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "50%",
                      animation: "float 6s ease-in-out infinite",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-30%",
                      left: "-15%",
                      width: "250px",
                      height: "250px",
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "50%",
                      animation: "float 8s ease-in-out infinite reverse",
                    }}
                  />

                  {/* Main Content */}
                  <div
                    style={{
                      position: "relative",
                      zIndex: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                      padding: "40px 30px",
                      color: "white",
                    }}
                  >
                    {/* Large Animated Heart */}
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "30px",
                        backdropFilter: "blur(10px)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                        animation: "pulse 2s infinite",
                      }}
                    >
                      <FaHeart
                        style={{
                          fontSize: "48px",
                          color: "white",
                          filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                        }}
                      />
                    </div>

                    {/* Title */}
                    <h2
                      style={{
                        fontSize: "32px",
                        fontWeight: "800",
                        margin: "0 0 12px 0",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        lineHeight: "1.2",
                      }}
                    >
                      Your wishlist is empty
                    </h2>

                    {/* Subtitle */}
                    <p
                      style={{
                        fontSize: "18px",
                        fontWeight: "400",
                        margin: "0 0 40px 0",
                        opacity: 0.95,
                        maxWidth: "350px",
                        lineHeight: "1.6",
                      }}
                    >
                      Discover amazing products and add them to your wishlist to
                      save for later
                    </p>

                    {/* Action Buttons */}
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => navigate("/")}
                        style={{
                          background: "rgba(255,255,255,0.95)",
                          color: "#1E293B",
                          border: "none",
                          padding: "16px 32px",
                          borderRadius: "50px",
                          fontSize: "16px",
                          fontWeight: "700",
                          cursor: "pointer",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                          transition: "all 0.3s ease",
                          backdropFilter: "blur(10px)",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 12px 35px rgba(0,0,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow =
                            "0 8px 25px rgba(0,0,0,0.2)";
                        }}
                      >
                        🛒 Start Shopping
                      </button>
                    </div>

                    {/* Stats */}
                    <div
                      style={{
                        marginTop: "40px",
                        display: "flex",
                        gap: "24px",
                        fontSize: "14px",
                        opacity: 0.8,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <div>❤️ 0 items saved</div>
                      <div>⭐ 4.8 avg rating</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{}} className="grid-layout wishlist-grid">
                  {wishlistProducts.map((item) => (
                    <ProductCard
                      key={item.id}
                      item={item}
                      onClick={() => handleProductClick(item)}
                      addToCart={handleAddToCart}
                      toggleWishlist={toggleWishlist}
                      isWishlisted={true}
                      isWishlistMode={true}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ width: "100%", marginTop: "200px" }}>
            <Footer />
          </div>

          {/* Your existing notification and cart components */}
          {/* Notification Toast */}
          {showNotification && (
            <div
              className="cart-notification"
              style={{
                position: "fixed",
                bottom: "110px",
                right: "30px",
                zIndex: 1001,
                background: "#10B981",
                color: "white",
                padding: "12px 20px",
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                animation: "slideIn 0.3s ease",
                minWidth: "200px",
              }}
            >
              <FaCheck style={{ fontSize: "16px" }} />
              <span style={{ fontSize: "14px", fontWeight: "600" }}>
                {notificationMessage}
              </span>
              <style>{`
                          @keyframes slideIn {
                            from {
                              transform: translateX(100%);
                              opacity: 0;
                            }
                            to {
                              transform: translateX(0);
                              opacity: 1;
                            }
                          }
                        `}</style>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default WishlistPage;
